import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { MessageSquare, RefreshCcw, CheckCheck, CircleDot, ExternalLink } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRealtime } from "@/hooks/useRealtime";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ""; }
}

export default function RecentSMSInbox() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("recent"); // "recent" | "unread"
  const nav = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/close-sms/inbox", {
        params: { limit: 8, unread_only: tab === "unread" },
      });
      setItems(data.items || []);
      setUnread(data.unread_count || 0);
    } catch (e) {
      // silent — don't break the dashboard
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  // Real-time: whenever a new inbound SMS notification arrives, refresh the widget
  useRealtime((evt) => {
    if (evt?.type === "notification" && evt.data?.kind === "sms_inbound") {
      load();
    }
  });

  const openClient = async (it) => {
    // Mark as read for the whole team → then jump to the client (if linked)
    try { await api.post(`/close-sms/inbox/${it.id}/read`); } catch {}
    if (it.client_id) {
      nav(`/admin/clients/${it.client_id}`);
    } else {
      toast.info("This reply isn't linked to a Client Quack client yet — check Close directly.");
      load();
    }
  };

  const markAllRead = async () => {
    try {
      const { data } = await api.post("/close-sms/inbox/read-all");
      toast.success(`Marked ${data.marked} as read`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <Card className="p-5 rounded-2xl border-slate-100 mb-6" data-testid="recent-sms-inbox">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
          <MessageSquare size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display font-bold text-lg">Recent Replies</h3>
            {unread > 0 && (
              <Badge className="bg-emerald-500 text-white border-0" data-testid="unread-count-badge">
                {unread} unread
              </Badge>
            )}
          </div>
          <p className="text-xs text-slate-500">Two-way SMS through Close · syncs every 60s</p>
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onClick={() => nav("/admin/sms")} className="text-xs text-emerald-700 hover:bg-emerald-50" data-testid="open-sms-inbox">
            Open inbox <ExternalLink size={12} className="ml-1" />
          </Button>
          <Button size="sm" variant="ghost" onClick={load} title="Refresh" data-testid="refresh-inbox"><RefreshCcw size={14} /></Button>
          {unread > 0 && (
            <Button size="sm" variant="ghost" onClick={markAllRead} className="text-xs" data-testid="mark-all-read">
              <CheckCheck size={12} className="mr-1" /> Mark all read
            </Button>
          )}
        </div>
      </div>

      <div className="flex gap-1 rounded-xl bg-slate-100 p-1 mb-3">
        {[
          { v: "recent", label: `Recent · ${items.length && tab === "recent" ? items.length : ""}`.trim() },
          { v: "unread", label: `Unread${unread ? " · " + unread : ""}` },
        ].map((b) => (
          <button
            key={b.v}
            onClick={() => setTab(b.v)}
            className={`flex-1 rounded-lg py-1.5 text-xs font-semibold transition ${tab === b.v ? "bg-white text-slate-900 shadow" : "text-slate-500"}`}
            data-testid={`inbox-tab-${b.v}`}
          >{b.label}</button>
        ))}
      </div>

      {loading && <p className="text-sm text-slate-400 text-center py-6">Loading…</p>}
      {!loading && items.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-6" data-testid="inbox-empty">
          {tab === "unread" ? "No unread replies — you're all caught up 🎉" : "No recent SMS replies yet."}
        </p>
      )}

      <div className="space-y-2">
        {items.map((it) => {
          const isUnread = !it.is_read;
          return (
            <button
              key={it.id}
              onClick={() => openClient(it)}
              className={`w-full text-left rounded-xl border p-3 transition ${isUnread ? "bg-emerald-50/60 border-emerald-200 hover:bg-emerald-50" : "bg-white border-slate-100 hover:bg-slate-50"}`}
              data-testid={`inbox-item-${it.id}`}
            >
              <div className="flex items-start gap-2">
                {isUnread && <CircleDot size={12} className="text-emerald-500 mt-1 shrink-0" data-testid="unread-dot" />}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`font-semibold truncate ${isUnread ? "text-slate-900" : "text-slate-700"}`}>
                      {it.client_name || it.from_number}
                    </span>
                    {!it.client_id && it.client_name === null && (
                      <Badge className="bg-slate-100 text-slate-500 border-0 text-[9px] uppercase tracking-widest">Unlinked</Badge>
                    )}
                    <span className="text-xs text-slate-400 ml-auto shrink-0">{timeAgo(it.date_created)}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-0.5 truncate">{it.text}</p>
                  {!it.client_name && <p className="text-[11px] text-slate-400 mt-0.5 font-mono">{it.from_number}</p>}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}
